/* --------------------------------------------------------------------------
 * StockApi.ts – helper functions for PACMaster stock‑manager service
 * --------------------------------------------------------------------------
 * Mirrors the style of AttributeApi.ts.  All calls use the CRA proxy pattern:
 *     "/service_stock_manager/<route>" → Flask service (30101)
 * --------------------------------------------------------------------------
 */

import { fetchData } from '../../../../utils/fetch';

/* --------------------------------------------------------------------------
 *  Types
 * --------------------------------------------------------------------------*/

/** Single stock‑movement line */
/** Single stock-movement line */
export interface StockMovementItem {
  /** Product ID */
  id_product:  number;
  /** Signed quantity (+inbound / –outbound) */
  qty:         number;
  /** Location ID where the movement occurs (optional at call‐site) */
  id_location?: number;       // ← make this optional
  /** Optional extra attributes saved as JSONB */
  attributes?: Record<string, unknown>;
}

/** Payload for /record_stock_movement */
export interface RecordStockMovementPayload {
  /** One or more movement lines.  The backend still supports legacy single‑item payloads but we *always* send an array. */
  items: StockMovementItem[];
}

export interface RecordStockMovementResultItem {
  row_id: number;
  id_product: number;
  id_location: number;
  qty: number;
  previous_level: number;
  new_level: number;
}

export interface RecordStockMovementResult {
  /** Monotonically increasing transaction number */
  txn_id: number;
  /** Summary of the items recorded */
  items: RecordStockMovementResultItem[];
  /** ISO‑8601 timestamp in UTC */
  timestamp: string;
  /** Execution latency as returned by the service (ms).  Optional – some deployments omit it. */
  execution_time_ms?: number;
}

/** Payload for /open_balance */
export interface OpenBalancePayload {
  id_product: number;
  qty: number;
  id_location: number;
}

export interface OpenBalanceResult {
  id_product: number;
  previous_level: number; // always 0.0 for an opening balance
  new_level: number;
  timestamp: string;
  execution_time_ms?: number;
}

/** Allowed values for GetStockHistoryPayload.direction */
export type StockDirection = 'inbound' | 'outbound';

/** Compression options accepted by the backend */
export interface StockCompression {
  /** Bucket aggregation kind */
  kind: 'sum' | 'avg';
  /** Time unit, e.g. 'minute' | 'hour' | 'day' */
  unit?: 'minute' | 'hour' | 'day';
}

/** Payload for /get_stock_history */
export interface GetStockHistoryPayload {
  /** Filter to these product IDs.  Omit or [] → all products. */
  id_products?: number[];
  /** Filter to these location IDs.  Omit → default [0] */
  id_locations?: number[];
  /** When true, service returns only the latest movement per product/location. */
  last_only?: boolean;
  /** ISO-date interval [start, end]. */
  date_range?: [string, string];
  /** Server-side aggregation; ignored when last_only === true */
  compression?: StockCompression;
  /** Direction filter */
  direction?: StockDirection;
}

export interface StockHistoryRecord {
  row_id: number;
  product_id: number;
  qty: number;
  timestamp: string;
  // NOTE: extra aggregate fields may appear when compression is used; keep index signature.
  [key: string]: unknown;
}

export interface GetStockHistoryResult {
  history: StockHistoryRecord[];
  execution_time_ms?: number;
}

/**
 * GET /service_stock/locations
 */
export interface Location {
  id: number;
  name: string;
  // include other fields returned by the API as needed
}

export async function listLocationsApi(): Promise<Location[]> {
  const locations = await fetchData('/service_stock/locations', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  return locations as Location[];
}

/**
 * POST /service_stock/create_location
 */
export interface CreateLocationPayload {
  /** Descriptive name for the new location */
  name: string;
  // add other fields to match your create_location payload
}

export interface CreateLocationResult {
  location: Location;
  execution_time_ms: number;
}

export async function createLocationApi(
  payload: CreateLocationPayload
): Promise<CreateLocationResult> {
  const raw = await fetchData('/service_stock/create_location', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  return {
    location: raw.location as Location,
    execution_time_ms: raw.execution_time_ms,
  } as CreateLocationResult;
}

/* --------------------------------------------------------------------------
 *  API helpers
 * --------------------------------------------------------------------------*/

/**
 * POST /service_stock/record_stock_movement
 */
export async function recordStockMovementApi(
  payload: RecordStockMovementPayload
): Promise<RecordStockMovementResult> {
  // Ensure we *always* send an array – simplifies the backend logic.
  if (!Array.isArray(payload.items) || payload.items.length === 0) {
    throw new Error('[recordStockMovementApi] "items" must be a non-empty array.');
  }

  // Inject default id_location = 0 if not provided
  const bodyPayload = {
    ...payload,
    items: payload.items.map(item => ({
      // first spread the entire item, then overwrite id_location if missing
      ...item,
      id_location: item.id_location ?? 0,
    })),
  };

  const raw = await fetchData('/service_stock/record_stock_movement', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(bodyPayload),
  });

  // The service may nest everything under movement.items, or directly under .items
  let movementData = raw.movement;
  if (
    movementData != null &&
    typeof movementData === 'object' &&
    Array.isArray((movementData as any).items)
  ) {
    movementData = (movementData as any).items;
  }
  const movementArray = Array.isArray(movementData)
    ? (movementData as any[])
    : Array.isArray((raw as any).items)
      ? (raw as any).items
      : [];

  const txn_id =
    raw.txn_id ??
    (movementArray.length > 0 ? movementArray[0].row_id / 1000 : undefined);

  if (movementArray.length === 0) {
    console.warn('[recordStockMovementApi] Unexpected response shape:', raw);
    throw new Error('Could not parse record_stock_movement response.');
  }

  return {
    txn_id,
    items: movementArray as RecordStockMovementResultItem[],
    timestamp: raw.timestamp ?? new Date().toISOString(),
    execution_time_ms: raw.execution_time_ms,
  } as RecordStockMovementResult;
}

/**
 * POST /service_stock/open_balance
 */
export async function openBalanceApi(payload: OpenBalancePayload): Promise<OpenBalanceResult> {
  const raw = await fetchData('/service_stock/open_balance', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const opening = raw.opening ?? raw;

  if (typeof opening?.id_product !== 'number') {
    console.warn('[openBalanceApi] Unexpected response shape:', raw);
    throw new Error('Could not parse open_balance response.');
  }

  return {
    id_product: opening.id_product,
    previous_level: opening.previous_level ?? 0,
    new_level: opening.new_level ?? payload.qty,
    timestamp: opening.timestamp ?? new Date().toISOString(),
    execution_time_ms: raw.execution_time_ms,
  } as OpenBalanceResult;
}

/**
 * POST /service_stock/get_stock_history
 */
export async function getStockHistoryApi(
  params: GetStockHistoryPayload
): Promise<GetStockHistoryResult> {
  // default to location 0 if none provided
  const bodyPayload = {
    ...params,
    id_locations: params.id_locations ?? [0],
  };

  const raw = await fetchData('/service_stock/get_stock_history', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(bodyPayload),
  });

  const history = raw.history ?? raw ?? [];
  if (!Array.isArray(history)) {
    console.warn('[getStockHistoryApi] Unexpected response shape:', raw);
    throw new Error('Could not parse get_stock_history response.');
  }

  return {
    history: history as StockHistoryRecord[],
    execution_time_ms: raw.execution_time_ms,
  } as GetStockHistoryResult;
}
